// src/hooks/useIntegration.js
import { useState, useEffect, useCallback } from "react";
import integrationService from "@/services/integrationService";

export default function useIntegration() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadProviders = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await integrationService.getWhatsAppProviders();

      // your service currently returns `res`, not `res.data`
      const data = res?.data ?? res;

      setProviders(Array.isArray(data) ? data : data || []);
    } catch (err) {
      console.error("Failed to load WhatsApp providers:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  const saveProvider = async (id, payload) => {
    if (id) {
      await integrationService.updateWhatsAppProvider(id, payload);
    } else {
      await integrationService.addWhatsAppProvider(payload);
    }

    // reload after save
    await loadProviders();
  };

  const deleteProvider = async (id) => {
    await integrationService.deleteWhatsAppProvider(id);

    // update UI locally (no need to refetch)
    setProviders((prev) => prev.filter((p) => p.id !== id));
  };

  const toggleProviderActive = async (row) => {
    if (!row?.id) return;

    const originalActive = row.active;

    // 🔹 optimistic UI update
    setProviders((prev) =>
      prev.map((p) =>
        p.id === row.id ? { ...p, active: !p.active } : p
      )
    );

    try {
      if (originalActive) {
        // currently active → deactivate
        await integrationService.deactivateStatus(row.id);
      } else {
        // currently inactive → activate
        await integrationService.activateStatus(row.id);
      }

      // optional: ensure in sync with backend
      // await loadProviders();
    } catch (err) {
      console.error("Failed to toggle active status:", err);

      // rollback on failure
      setProviders((prev) =>
        prev.map((p) =>
          p.id === row.id ? { ...p, active: originalActive } : p
        )
      );
    }
  };

  return {
    providers,
    loading,
    error,
    saveProvider,
    deleteProvider,
    toggleProviderActive,
    reload: loadProviders,
  };
}
